import type { Annotation } from 'api';

const DESKTOP_MAX_WIDTH = 2560;
const MOBILE_MAX_WIDTH = 325;
const TARGET_MAX_HEIGHT = 650;

export interface MobileZoom {
  isZooming: boolean;
  scale: number;
  translateX: number;
  translateY: number;
}

export interface CanvasConfig {
  devicePixelRatio: number;
  scaledWidth: number;
  scaledHeight: number;
  scale: number;
}

export interface CanvasDimensions {
  width: number;
  height: number;
  scale: number;
}

export interface RenderCanvasOptions {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  imageSrc: string;
  annotations: Annotation[];
  hiddenCategories: string[];
  labelColourMap: Map<string, readonly number[]>;
  isMobile: boolean;
  shouldDarken: boolean;
  zoom?: MobileZoom;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
  onError?: () => void;
}

export const calculateCanvasDimensions = (
  img: HTMLImageElement,
  maxWidth: number,
  maxHeight: number
): CanvasDimensions => {
  const widthScale = maxWidth / img.width;
  const heightScale = maxHeight / img.height;
  const scale = Math.min(widthScale, heightScale);

  return {
    height: img.height * scale,
    scale,
    width: img.width * scale,
  };
};

export const configureCanvas = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  dimensions: CanvasDimensions
): CanvasConfig => {
  const devicePixelRatio = window.devicePixelRatio || 1;
  const { width: scaledWidth, height: scaledHeight } = dimensions;

  // Set canvas internal resolution for high-DPI displays
  canvas.width = scaledWidth * devicePixelRatio;
  canvas.height = scaledHeight * devicePixelRatio;

  // Scale canvas back down using CSS
  canvas.style.width = `${scaledWidth}px`;
  canvas.style.height = `${scaledHeight}px`;

  // Scale the drawing context
  ctx.scale(devicePixelRatio, devicePixelRatio);

  // Configure for high quality
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  return {
    devicePixelRatio,
    scale: dimensions.scale,
    scaledHeight,
    scaledWidth,
  };
};

export const drawSegment = (
  ctx: CanvasRenderingContext2D,
  segment: number[],
  colour: string,
  scale: number,
  lineWidth: number
): void => {
  ctx.strokeStyle = colour;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();

  for (let i = 0; i < segment.length; i += 2) {
    const x = segment[i] * scale;
    const y = segment[i + 1] * scale;
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }

  ctx.closePath();
  ctx.stroke();
};

export const createMaskCanvas = (
  width: number,
  height: number,
  devicePixelRatio: number,
  fillStyle: string
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null => {
  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = width;
  maskCanvas.height = height;

  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) return null;

  // Configure mask canvas to match main canvas scaling
  maskCtx.scale(devicePixelRatio, devicePixelRatio);
  maskCtx.imageSmoothingEnabled = true;
  maskCtx.imageSmoothingQuality = 'high';
  maskCtx.fillStyle = fillStyle;
  maskCtx.fillRect(0, 0, width / devicePixelRatio, height / devicePixelRatio);

  return { canvas: maskCanvas, ctx: maskCtx };
};

export const drawSegmentationMask = (
  maskCtx: CanvasRenderingContext2D,
  annotations: Annotation[],
  scale: number,
  hiddenCategories: string[]
): void => {
  maskCtx.globalCompositeOperation = 'destination-out';

  for (const { segmentation, label } of annotations) {
    if (hiddenCategories.includes(label)) continue;

    for (const segment of segmentation) {
      maskCtx.beginPath();

      for (let i = 0; i < segment.length; i += 2) {
        const x = segment[i] * scale;
        const y = segment[i + 1] * scale;
        i === 0 ? maskCtx.moveTo(x, y) : maskCtx.lineTo(x, y);
      }

      maskCtx.closePath();
      maskCtx.fill();
    }
  }
};

export const getTouchDistance = (touches: TouchList): number => {
  if (touches.length < 2) return 0;
  const touch1 = touches[0];
  const touch2 = touches[1];
  return Math.sqrt(Math.pow(touch2.clientX - touch1.clientX, 2) + Math.pow(touch2.clientY - touch1.clientY, 2));
};

export const getTouchCenter = (touches: TouchList): { x: number; y: number } => {
  if (touches.length < 2) return { x: 0, y: 0 };
  const touch1 = touches[0];
  const touch2 = touches[1];
  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  };
};

export const applyZoomTransform = (
  ctx: CanvasRenderingContext2D,
  zoom: MobileZoom,
  canvasWidth: number,
  canvasHeight: number
): void => {
  if (zoom.scale > 1) {
    ctx.save();
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    ctx.translate(centerX + zoom.translateX, centerY + zoom.translateY);
    ctx.scale(zoom.scale, zoom.scale);
    ctx.translate(-centerX, -centerY);
  }
};

export const restoreZoomTransform = (ctx: CanvasRenderingContext2D, zoom: MobileZoom): void => {
  if (zoom.scale > 1) {
    ctx.restore();
  }
};

export const drawAnnotationsWithDarkening = (
  ctx: CanvasRenderingContext2D,
  annotations: Annotation[],
  config: CanvasConfig,
  hiddenCategories: string[],
  labelColourMap: Map<string, readonly number[]>,
  formatRgb: (rgb: number[]) => string,
  isMobile: boolean,
  outsideDarknessLevel = 0.7
): void => {
  const { devicePixelRatio, scaledWidth, scaledHeight, scale } = config;

  const maskResult = createMaskCanvas(
    scaledWidth * devicePixelRatio,
    scaledHeight * devicePixelRatio,
    devicePixelRatio,
    `rgba(0, 0, 0, ${outsideDarknessLevel})`
  );

  if (!maskResult) return;

  const { canvas: maskCanvas, ctx: maskCtx } = maskResult;

  drawSegmentationMask(maskCtx, annotations, scale, hiddenCategories);

  for (const { segmentation, label, rgb_colour } of annotations) {
    if (hiddenCategories.includes(label)) continue;

    const colourToUse = labelColourMap.get(label) || rgb_colour;
    const strokeColour = formatRgb([...colourToUse]);
    const lineWidth = isMobile ? 2 : 3;

    for (const segment of segmentation) {
      drawSegment(ctx, segment, strokeColour, scale, lineWidth);
    }
  }

  ctx.drawImage(maskCanvas, 0, 0, scaledWidth, scaledHeight);
};

export const drawAnnotationsOnly = (
  ctx: CanvasRenderingContext2D,
  annotations: Annotation[],
  config: CanvasConfig,
  hiddenCategories: string[],
  labelColourMap: Map<string, readonly number[]>,
  formatRgb: (rgb: number[]) => string,
  isMobile: boolean
): void => {
  const { scale } = config;

  for (const { segmentation, label, rgb_colour } of annotations) {
    if (hiddenCategories.includes(label)) continue;

    const colourToUse = labelColourMap.get(label) || rgb_colour;
    const strokeColour = formatRgb([...colourToUse]);
    const lineWidth = isMobile ? 2 : 3;

    for (const segment of segmentation) {
      drawSegment(ctx, segment, strokeColour, scale, lineWidth);
    }
  }
};

export const applyEnhancementFilter = (ctx: CanvasRenderingContext2D) => {
  ctx.filter = 'contrast(1) brightness(1) saturate(1)';
};

export const resetFilter = (ctx: CanvasRenderingContext2D) => {
  ctx.filter = 'none';
};

export const formatRgb = (rgb: number[]): string => `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;

export const setupZoomCanvas = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, size = 250) => {
  const devicePixelRatio = window.devicePixelRatio || 1;
  canvas.width = size * devicePixelRatio;
  canvas.height = size * devicePixelRatio;
  ctx.scale(devicePixelRatio, devicePixelRatio);
};

export const drawAnnotationsWithTransform = (
  context: CanvasRenderingContext2D,
  annotations: Annotation[],
  hiddenCategories: string[],
  labelColourMap: Map<string, readonly number[]>,
  sourceX: number,
  sourceY: number,
  sourceWidth: number,
  sourceHeight: number,
  canvasSize = 250
) => {
  context.save();

  const scaleX = canvasSize / sourceWidth;
  const scaleY = canvasSize / sourceHeight;

  context.scale(scaleX, scaleY);
  context.translate(-sourceX, -sourceY);

  for (const { segmentation, label, rgb_colour } of annotations) {
    if (hiddenCategories.includes(label)) continue;

    const colourToUse = labelColourMap.get(label) || rgb_colour;
    const strokeColour = formatRgb([...colourToUse]);
    const lineWidth = 4 / Math.min(scaleX, scaleY);

    for (const segment of segmentation) {
      context.strokeStyle = strokeColour;
      context.lineWidth = lineWidth;
      context.beginPath();

      for (let i = 0; i < segment.length; i += 2) {
        const x = segment[i];
        const y = segment[i + 1];

        if (i === 0) {
          context.moveTo(x, y);
        } else {
          context.lineTo(x, y);
        }
      }

      context.closePath();
      context.stroke();
    }
  }

  context.restore();
};

export const drawImageWithFallback = (
  ctx: CanvasRenderingContext2D,
  sourceCanvas: HTMLCanvasElement,
  sourceRect: DOMRect,
  mousePosition: { x: number; y: number },
  zoomLevel: number,
  canvasSize = 250
) => {
  const canvasX = mousePosition.x - sourceRect.left;
  const canvasY = mousePosition.y - sourceRect.top;
  const zoomRadius = 50 / zoomLevel;

  const sourceCanvasDevicePixelRatio = sourceCanvas.width / sourceRect.width;
  let sourceX = (canvasX - zoomRadius) * sourceCanvasDevicePixelRatio;
  let sourceY = (canvasY - zoomRadius) * sourceCanvasDevicePixelRatio;
  const sourceWidth = zoomRadius * 2 * sourceCanvasDevicePixelRatio;
  const sourceHeight = zoomRadius * 2 * sourceCanvasDevicePixelRatio;

  sourceX = Math.max(0, Math.min(sourceX, sourceCanvas.width - sourceWidth));
  sourceY = Math.max(0, Math.min(sourceY, sourceCanvas.height - sourceHeight));

  ctx.clearRect(0, 0, canvasSize, canvasSize);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  try {
    applyEnhancementFilter(ctx);
    ctx.drawImage(sourceCanvas, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvasSize, canvasSize);
    resetFilter(ctx);
  } catch {
    resetFilter(ctx);
  }
};

export const renderCanvas = (options: RenderCanvasOptions) => {
  const {
    canvas,
    ctx,
    imageSrc,
    annotations,
    hiddenCategories,
    labelColourMap,
    isMobile,
    shouldDarken,
    zoom,
    onLoadStart,
    onLoadEnd,
    onError,
  } = options;

  onLoadStart?.();

  const targetMaxWidth = isMobile ? MOBILE_MAX_WIDTH : DESKTOP_MAX_WIDTH;

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = imageSrc;

  img.onload = () => {
    onLoadEnd?.();

    const dimensions = calculateCanvasDimensions(img, targetMaxWidth, TARGET_MAX_HEIGHT);
    const config = configureCanvas(canvas, ctx, dimensions);

    ctx.clearRect(0, 0, config.scaledWidth, config.scaledHeight);

    if (zoom && isMobile) {
      applyZoomTransform(ctx, zoom, config.scaledWidth, config.scaledHeight);
    }

    ctx.drawImage(img, 0, 0, config.scaledWidth, config.scaledHeight);

    if (annotations.length > 0) {
      if (shouldDarken) {
        drawAnnotationsWithDarkening(ctx, annotations, config, hiddenCategories, labelColourMap, formatRgb, isMobile);
      } else {
        drawAnnotationsOnly(ctx, annotations, config, hiddenCategories, labelColourMap, formatRgb, isMobile);
      }
    }

    if (zoom && isMobile) {
      restoreZoomTransform(ctx, zoom);
    }
  };

  img.onerror = () => {
    onLoadEnd?.();
    onError?.();
  };
};
